"use client";

import { ClipboardList, LineChart, RefreshCw } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/shared/lib/utils";
import {
  POSITION_PATHS,
  normalizeLolRole,
  type PositionPath,
} from "./role-paths";

/**
 * Иконки игровых ролей (позиций) LoL — официальные path'ы позиций Riot
 * (как на op.gg/tryouts.gg), отрисованные currentColor'ом, чтобы красить
 * текстовыми токенами. Coach/Analyst/Substitute — lucide в том же размере.
 */

export { normalizeLolRole };

export type LolRoleKey =
  | "top"
  | "jungle"
  | "mid"
  | "adc"
  | "support"
  | "fill"
  | "coach"
  | "analyst"
  | "substitute";

/** Цвет роли — согласован с role-* вариантами Badge и AvatarWithRoleBadge */
export const ROLE_TEXT_COLOR: Record<LolRoleKey, string> = {
  top: "text-purple-400",
  jungle: "text-green-400",
  mid: "text-red-400",
  adc: "text-yellow-400",
  support: "text-blue-400",
  fill: "text-muted-foreground",
  coach: "text-teal-400",
  analyst: "text-cyan-400",
  substitute: "text-muted-foreground",
};


/**
 * Название роли на языке интерфейса (KAN-1267). Ключ берётся из любого
 * написания роли; неизвестное значение возвращается как пришло — это важно
 * для бейджей, где рядом с ролью лежит ранг.
 */
export function useRoleLabel(): (role: string | null | undefined) => string {
  const t = useTranslations("Common.roles");
  return (role) => {
    const key = normalizeLolRole(role);
    return key ? t(key) : (role ?? "");
  };
}

export interface RoleIconProps {
  role: string | null | undefined;
  /** px, по умолчанию 16 */
  size?: number;
  /** Покрасить в фирменный цвет роли. По умолчанию false — нейтральный серый. */
  colored?: boolean;
  className?: string;
}

export const RoleIcon = ({
  role,
  size = 16,
  colored = false,
  className,
}: RoleIconProps) => {
  const roleLabel = useRoleLabel();
  const key = normalizeLolRole(role);
  if (!key) return null;

  // По умолчанию иконки серые (как на tryouts.gg / op.gg), а не золотые из исходника.
  const colorCls = colored ? ROLE_TEXT_COLOR[key] : "text-muted-foreground";

  if (key === "coach") {
    return (
      <ClipboardList
        style={{ width: size, height: size }}
        className={cn("shrink-0", colorCls, className)}
        aria-label={roleLabel(key)}
      />
    );
  }
  if (key === "analyst") {
    return (
      <LineChart
        style={{ width: size, height: size }}
        className={cn("shrink-0", colorCls, className)}
        aria-label={roleLabel(key)}
      />
    );
  }
  if (key === "substitute") {
    return (
      <RefreshCw
        style={{ width: size, height: size }}
        className={cn("shrink-0", colorCls, className)}
        aria-label={roleLabel(key)}
      />
    );
  }

  // key здесь уже только позиция: coach/analyst/substitute отработали выше.
  const paths = (POSITION_PATHS as Partial<Record<LolRoleKey, PositionPath>>)[key];
  if (!paths) return null;

  return (
    <svg
      viewBox="0 0 34 34"
      width={size}
      height={size}
      className={cn("shrink-0", colorCls, className)}
      aria-label={roleLabel(key)}
      role="img"
    >
      {paths.dim && (
        <path opacity="0.45" fill="currentColor" fillRule="evenodd" d={paths.dim} />
      )}
      {paths.activeIsPolygon ? (
        <polygon fill="currentColor" points={paths.active} />
      ) : (
        <path fill="currentColor" fillRule="evenodd" d={paths.active} />
      )}
    </svg>
  );
};

/** Иконка + подпись роли в одну строку (для профиля, ростеров, списков) */
export const RoleInline = ({
  role,
  size = 16,
  className,
  labelClassName,
}: RoleIconProps & { labelClassName?: string }) => {
  const roleLabel = useRoleLabel();
  const key = normalizeLolRole(role);
  if (!key) return null;
  return (
    <span className={cn("inline-flex items-center gap-1.5 min-w-0", className)}>
      <RoleIcon role={key} size={size} />
      <span className={cn("truncate", labelClassName)}>{roleLabel(key)}</span>
    </span>
  );
};
